import { AllCyclesResponse, ChartViewType, CompletedCyclesSeries } from '@/api/dashboard/types';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { QueryDateRange } from '../../../types';
import {
	formatDayMonthLabel,
	formatDayNameLabel,
	formatMonthLabel,
	generateDaysRangeLabels,
	generateMonthsRangeLabels,
	generateWeekRangeLabels,
	transformSeriesDataset,
	CompletedCyclesDataset,
	normalizeDataset,
	generateOneDayRangeLabels,
	formatHoursLabel,
	LabelInfo,
} from './utils';

interface CompletedCyclesChartData {
	labels?: LabelInfo[];
	dataset?: CompletedCyclesDataset[];
}

const normalizeData = (
	series: CompletedCyclesSeries[] | undefined,
	formatFn: (date: dayjs.Dayjs, start: dayjs.Dayjs, end: dayjs.Dayjs) => string,
	labels: LabelInfo[],
	startDate: dayjs.Dayjs,
	endDate: dayjs.Dayjs
) => {
	const periodLength = endDate.diff(startDate, 'day') + 1; // Include both start and end dates

	const normalizedSeries = series?.reduce((acc, currentSeries, index) => {
		// For previous period series (index > 0), shift the dates forward to align with current period labels
		const isPreviousPeriod = index > 0;
		const shiftedSeries = isPreviousPeriod
			? {
					...currentSeries,
					points: currentSeries.points.map((point) => ({
						...point,
						date: dayjs(point.date).add(periodLength, 'day').toISOString(),
					})),
				}
			: currentSeries;

		const dataset = transformSeriesDataset(formatFn, startDate, endDate, shiftedSeries);
		const normalizedDataset = normalizeDataset(labels, dataset);
		acc.push(normalizedDataset);
		return acc;
	}, [] as CompletedCyclesDataset[]);
	return normalizedSeries;
};

export const useCompletedCyclesChartData = (
	completedCycles?: AllCyclesResponse,
	dateRange?: QueryDateRange
): CompletedCyclesChartData => {
	const [labels, setLabels] = useState<LabelInfo[]>();
	const [dataset, setDataset] = useState<CompletedCyclesDataset[]>();

	useEffect(() => {
		const { data: series, view } = completedCycles || {};
		const { start, end } = dateRange || {};

		if (!start || !end) return;

		const startDate = dayjs(start);
		const endDate = dayjs(end);
		const dayDifference = endDate?.diff(startDate, 'd');

		if (view === ChartViewType.Hours) {
			// For hours view, filter points to show only the relevant day
			// Index 0 is current period (filter by startDate), index 1+ is previous period (filter by previous day)
			const filteredSeries = series?.map((s, index) => {
				const targetDate = index === 0 ? startDate : startDate.subtract(1, 'day');
				return {
					...s,
					points: s.points.filter((log) => dayjs(log.date).isSame(targetDate, 'day')),
				};
			});

			const labels = generateOneDayRangeLabels(startDate, endDate);
			const normalizedSeries = normalizeData(
				filteredSeries,
				formatHoursLabel,
				labels,
				startDate,
				endDate
			);

			setLabels(labels);
			setDataset(normalizedSeries);
			return;
		}

		if (view === ChartViewType.Days) {
			const formatFn = dayDifference === 6 ? formatDayNameLabel : formatDayMonthLabel;
			const labels = generateDaysRangeLabels(startDate, endDate);
			const normalizedSeries = normalizeData(series, formatFn, labels, startDate, endDate);

			setLabels(labels);
			setDataset(normalizedSeries);
			return;
		}

		if (view === ChartViewType.Weeks) {
			const labels = generateWeekRangeLabels(startDate, endDate);
			const normalizedSeries = normalizeData(
				series,
				formatDayMonthLabel,
				labels,
				startDate,
				endDate
			);

			setLabels(labels);
			setDataset(normalizedSeries);
			return;
		}

		const labels = generateMonthsRangeLabels(startDate, endDate);
		const normalizedSeries = normalizeData(series, formatMonthLabel, labels, startDate, endDate);

		setLabels(labels);
		setDataset(normalizedSeries);
	}, [completedCycles, dateRange]);

	return { labels, dataset };
};
